import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import {
  SvgConversionError,
  convertSvg,
  downloadBlob,
  formatSize,
  outputFilename,
  prepareSvgFile,
  type LoadedSvg,
  type SvgConversionResult,
  type SvgOutputFormat,
} from '../model/svgProcessing'
import { ToolHero } from '@/shared/ui/ToolHero'

const UTILITY_KEY = 'svg-converter'

function clampDimension(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.min(12000, Math.max(1, Math.round(value)))
}

function sizeDelta(original: number, result: number): number {
  if (original <= 0) return 0
  return Math.round(((result - original) / original) * 100)
}

function formatLabel(format: SvgOutputFormat): string {
  return format === 'jpg' ? 'JPG' : 'PNG'
}

export function SvgConverterTool() {
  const t = useT()
  const copy = t.svgConverter
  const [svg, setSvg] = useState<LoadedSvg | null>(null)
  const [format, setFormat] = useState<SvgOutputFormat>('png')
  const [useOriginalSize, setUseOriginalSize] = useState(true)
  const [keepAspect, setKeepAspect] = useState(true)
  const [targetWidth, setTargetWidth] = useState(1)
  const [targetHeight, setTargetHeight] = useState(1)
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [result, setResult] = useState<SvgConversionResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    return () => {
      if (svg) {
        URL.revokeObjectURL(svg.url)
      }
    }
  }, [svg])

  useEffect(() => {
    return () => {
      if (result) {
        URL.revokeObjectURL(result.url)
      }
    }
  }, [result])

  const clearResult = useCallback(() => {
    setResult(null)
  }, [])

  const activeWidth = svg ? (useOriginalSize ? svg.width : targetWidth) : targetWidth
  const activeHeight = svg ? (useOriginalSize ? svg.height : targetHeight) : targetHeight
  const aspectRatio = svg ? svg.width / svg.height : 1
  const resultDelta = result && svg ? sizeDelta(svg.file.size, result.blob.size) : null
  const canConvert = svg !== null && !isProcessing && activeWidth > 0 && activeHeight > 0

  const loadFile = useCallback(
    async (file: File) => {
      setError(null)
      clearResult()

      try {
        const loaded = await prepareSvgFile(file, copy.errorsText)
        setSvg(loaded)
        setUseOriginalSize(true)
        setKeepAspect(true)
        setTargetWidth(loaded.width)
        setTargetHeight(loaded.height)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : copy.errorsText.invalidSvg
        const code = err instanceof SvgConversionError ? err.code : 'invalid_svg'
        setSvg(null)
        setError(message)
        toolError(UTILITY_KEY, code)
      }
    },
    [clearResult, copy.errorsText],
  )

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      const [file] = Array.from(incoming)
      if (file) {
        void loadFile(file)
      }
    },
    [loadFile],
  )

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        addFiles(event.target.files)
        event.target.value = ''
      }
    },
    [addFiles],
  )

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }, [])

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setDropActive(false)

      if (event.dataTransfer.files.length > 0) {
        addFiles(event.dataTransfer.files)
      }
    },
    [addFiles],
  )

  const setOutputFormat = useCallback(
    (nextFormat: SvgOutputFormat) => {
      setFormat(nextFormat)
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const handleUseOriginalChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!svg) return
      const checked = event.target.checked
      setUseOriginalSize(checked)
      setError(null)
      clearResult()
      if (!checked) {
        setTargetWidth(svg.width)
        setTargetHeight(svg.height)
      }
    },
    [clearResult, svg],
  )

  const handleKeepAspectChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setKeepAspect(event.target.checked)
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const handleWidthChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!svg) return
      const width = clampDimension(Number(event.target.value))
      const height = keepAspect ? clampDimension(width / aspectRatio) : targetHeight
      setTargetWidth(width)
      setTargetHeight(height)
      setUseOriginalSize(false)
      setError(null)
      clearResult()
    },
    [aspectRatio, clearResult, keepAspect, svg, targetHeight],
  )

  const handleHeightChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!svg) return
      const height = clampDimension(Number(event.target.value))
      const width = keepAspect ? clampDimension(height * aspectRatio) : targetWidth
      setTargetWidth(width)
      setTargetHeight(height)
      setUseOriginalSize(false)
      setError(null)
      clearResult()
    },
    [aspectRatio, clearResult, keepAspect, svg, targetWidth],
  )

  const handleBackgroundChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setBackgroundColor(event.target.value)
      setError(null)
      clearResult()
    },
    [clearResult],
  )

  const handleConvert = useCallback(async () => {
    if (!svg) return

    const width = activeWidth
    const height = activeHeight
    const startTime = performance.now()
    toolInvoke(UTILITY_KEY, {
      format,
      width,
      height,
      mode: useOriginalSize ? 'original' : 'custom',
    })
    setIsProcessing(true)
    setError(null)
    clearResult()

    try {
      const nextResult = await convertSvg(
        svg,
        format,
        width,
        height,
        backgroundColor,
        copy.errorsText,
      )
      setResult(nextResult)
      toolSuccess(UTILITY_KEY, Math.round(performance.now() - startTime), {
        format,
        width,
        height,
        mode: useOriginalSize ? 'original' : 'custom',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : copy.errorsText.conversion
      const code = err instanceof SvgConversionError ? err.code : 'conversion_failed'
      setError(message)
      toolError(UTILITY_KEY, code, { format, width, height })
    } finally {
      setIsProcessing(false)
    }
  }, [
    activeHeight,
    activeWidth,
    backgroundColor,
    clearResult,
    copy.errorsText,
    format,
    svg,
    useOriginalSize,
  ])

  const handleDownload = useCallback(() => {
    if (!svg || !result) return
    downloadBlob(result.blob, outputFilename(svg.file.name, result.format))
  }, [result, svg])

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['SVG -> PNG/JPG', copy.maxFileSize]}
        collapsed={Boolean(svg)}
      />

      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          svg && 'lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start',
        )}
      >
        <div className="min-w-0 space-y-6">
          <Card
            className={cn(
              'mx-auto max-w-3xl transition-colors',
              svg && 'max-w-none',
              dropActive && 'border-primary bg-primary/5',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent className="p-4 text-center sm:p-8">
              <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-xs font-semibold text-muted-foreground">
                SVG
              </div>
              <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
              <p
                className={cn(
                  'mx-auto mb-5 max-w-md text-sm leading-6 text-muted-foreground',
                  svg && 'hidden sm:block',
                )}
              >
                {copy.dropDescription}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".svg,image/svg+xml"
                disabled={isProcessing}
                className="hidden"
                onChange={handleInputChange}
              />
              <Button
                disabled={isProcessing}
                onClick={() => inputRef.current?.click()}
              >
                {svg ? copy.replaceFile : copy.chooseFile}
              </Button>
            </CardContent>
          </Card>

          {svg && (
            <Card className="overflow-hidden">
              <CardContent className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-semibold tracking-normal" title={svg.file.name}>
                      {svg.file.name}
                    </h2>
                    <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
                      <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-muted-foreground">
                        SVG
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {formatSize(svg.file.size)}
                      </span>
                      <span className="rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground">
                        {svg.width} x {svg.height}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  <PreviewPanel
                    label={copy.sourceLabel}
                    src={svg.url}
                    name={svg.file.name}
                    size={formatSize(svg.file.size)}
                    dimensions={`${svg.width} x ${svg.height}`}
                  />
                  {result ? (
                    <PreviewPanel
                      label={copy.resultLabel}
                      src={result.url}
                      name={outputFilename(svg.file.name, result.format)}
                      size={formatSize(result.blob.size)}
                      dimensions={`${result.width} x ${result.height}`}
                      delta={resultDelta}
                    />
                  ) : (
                    <div className="overflow-hidden rounded-md border bg-background">
                      <div className="grid aspect-video place-items-center bg-muted">
                        <span className="rounded-md border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground">
                          {formatLabel(format)}
                        </span>
                      </div>
                      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
                        <div>
                          <div className="text-xs text-muted-foreground">{copy.resultLabel}</div>
                          <div className="text-sm font-medium">{copy.waitingPreview}</div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
                            {activeWidth} x {activeHeight}
                          </span>
                          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
                            {copy.notReady}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {svg && (
          <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
            <Card>
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold tracking-normal">{copy.settings}</h2>
                  <LocalizedProcessingBadge variant="local" />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">{copy.outputFormat}</div>
                  <div className="grid grid-cols-2 overflow-hidden rounded-md border">
                    {(['png', 'jpg'] as const).map((item) => (
                      <button
                        key={item}
                        type="button"
                        disabled={isProcessing}
                        className={cn(
                          'px-3 py-2 text-sm font-medium transition-colors',
                          item === 'jpg' && 'border-l',
                          format === item
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-background hover:bg-muted',
                        )}
                        onClick={() => setOutputFormat(item)}
                      >
                        {formatLabel(item)}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                  <span className="text-sm font-medium">{copy.keepOriginalSize}</span>
                  <input
                    type="checkbox"
                    checked={useOriginalSize}
                    disabled={isProcessing}
                    onChange={handleUseOriginalChange}
                    className="h-4 w-4 accent-primary"
                  />
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-2">
                    <span className="text-sm font-medium">{copy.width}</span>
                    <input
                      type="number"
                      min={1}
                      max={12000}
                      step={1}
                      value={activeWidth}
                      disabled={isProcessing || useOriginalSize}
                      onChange={handleWidthChange}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-sm font-medium">{copy.height}</span>
                    <input
                      type="number"
                      min={1}
                      max={12000}
                      step={1}
                      value={activeHeight}
                      disabled={isProcessing || useOriginalSize}
                      onChange={handleHeightChange}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                    />
                  </label>
                </div>

                <label className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                  <span className="text-sm font-medium">{copy.keepAspect}</span>
                  <input
                    type="checkbox"
                    checked={keepAspect}
                    disabled={isProcessing || useOriginalSize}
                    onChange={handleKeepAspectChange}
                    className="h-4 w-4 accent-primary"
                  />
                </label>

                {format === 'jpg' && (
                  <label className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2">
                    <span className="text-sm font-medium">{copy.backgroundColor}</span>
                    <input
                      type="color"
                      value={backgroundColor}
                      disabled={isProcessing}
                      onChange={handleBackgroundChange}
                      className="h-9 w-12 rounded border bg-background p-1"
                    />
                  </label>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  disabled={!canConvert}
                  onClick={handleConvert}
                >
                  {isProcessing ? copy.processing : copy.convert}
                </Button>

                {result && (
                  <Button className="w-full" variant="outline" onClick={handleDownload}>
                    {copy.download}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-5">
                <h2 className="text-lg font-semibold tracking-normal">{copy.summary}</h2>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between gap-3">
                    <span>{copy.originalSize}</span>
                    <strong className="font-medium text-foreground">
                      {formatSize(svg.file.size)}
                    </strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span>{copy.targetSize}</span>
                    <strong className="font-medium text-foreground">
                      {activeWidth} x {activeHeight}
                    </strong>
                  </div>
                  {result && (
                    <div className="flex justify-between gap-3">
                      <span>{copy.resultSize}</span>
                      <strong className="font-medium text-foreground">
                        {formatSize(result.blob.size)}
                      </strong>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <p className="rounded-md border bg-card px-3 py-2 text-xs leading-5 text-muted-foreground">
              {copy.privacyNote}
            </p>
          </aside>
        )}
      </div>

      <section className="mt-12 grid gap-4 border-t pt-8 lg:grid-cols-3">
        {copy.faq.map((item) => (
          <div key={item.question}>
            <h2 className="text-base font-semibold tracking-normal">{item.question}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.answer}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}

function PreviewPanel({
  label,
  src,
  name,
  size,
  dimensions,
  delta,
}: {
  label: string
  src: string
  name: string
  size: string
  dimensions: string
  delta?: number | null
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-background">
      <div className="aspect-video bg-muted">
        <img src={src} alt={name} className="h-full w-full object-contain" />
      </div>
      <div className="space-y-1 p-2 sm:space-y-3 sm:p-3">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="truncate text-sm font-medium" title={name}>
            {name}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {dimensions}
          </span>
          <span className="rounded-md border bg-card px-2 py-1 text-xs text-muted-foreground">
            {size}
            {typeof delta === 'number' && (
              <span
                className={cn(
                  'ml-1 font-medium',
                  delta <= 0 ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                {delta > 0 ? '+' : ''}
                {delta}%
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
